import React from 'react';
import { motion, useAnimation } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaTrophy, FaCheckCircle, FaFileAlt, FaHandshake, FaChevronRight, FaLeaf, FaTint, FaCogs, FaFlask, FaFingerprint } from 'react-icons/fa';
import './css/Hero.css';
import Footer from './Footer';
import SEO from '../components/SEO';
import { organizationSchema, websiteSchema } from '../components/StructuredData';
import aromaticImage from '../assets/images/SBU- Aromatic Chemicals & derivatives.jpg';
import AdditivesImage from '../assets/images/file-5MGcwhG6tWWUuTpB9aUGWZ.webp';
import HeatDissipationImage from '../assets/images/Heat Transfer Image-Nimble Technologies -1 (1).png';
import GreenMetalWorkingImage from '../assets/images/MWF Home Pagee- Green Metal Working Addtives .png';
import ecoFriendlyImage from '../assets/images/Landing Image.jpg';
import aboutUsImage from '../assets/images/About us img.png';
import contactUsImage from '../assets/images/contact us image.jpg';

// Import customer logos
import benningerLogo from '../assets/images/customer-logos/BENNINGER.png';
import bestLogo from '../assets/images/customer-logos/BEST Undertaking.png';
import bharatElectronicsLogo from '../assets/images/customer-logos/bharat-electronics-Logo.webp';
import grasimLogo from '../assets/images/customer-logos/Grasim.jpg';
import hondaLogo from '../assets/images/customer-logos/Honda.webp';
import kirloskarLogo from '../assets/images/customer-logos/Kirloskar.jpg';
import mdlLogo from '../assets/images/customer-logos/MDL Logo.png';
import rcfLogo from '../assets/images/customer-logos/RCF.png';

// Add Google Fonts
import { Helmet } from 'react-helmet';

const FontLoader = () => (
  <Helmet>
    <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&family=Raleway:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
  </Helmet>
);

const TourCard = ({ title, description, price, isPopular, imageSrc, link }) => (
  <div className="tour-card">
    {isPopular && <div className="tour-badge">OUR BEST SELLER</div>}
    <div className="tour-image">
      <img 
        src={imageSrc} 
        alt={title}
      />
    </div>
    <div className="tour-content">
      <h3 className="tour-title">{title}</h3>
      <p className="tour-description">{description}</p>
      <div className="tour-actions">
        <Link to={link}>
          <button className="read-more-btn">Read More <span className="arrow">&rarr;</span></button>
        </Link>
      </div>
    </div>
  </div>
);

const AboutSection = () => (
  <section className="about-section">
    <div className="about-container">
      <motion.div 
        className="about-content"
        initial={{ opacity: 0, x: -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="section-header">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            About Us
          </motion.h2>
          {/* <p className="section-subtitle">Pioneering Green Chemistry Solutions Since 1996</p> */}
        </div>
        <p className="about-description content-justified">
          We are technocrats driven by science, engineering, and a passion for creating eco-conscious solutions.
          With 30 years of expertise, we deliver customized chemical solutions based on green chemistry principles — designed to elevate EHS standards, enhance sustainability, and optimize industrial processes.
        </p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Link to="/about" className="know-more-btn">
            Know More About Us <span className="arrow">&rarr;</span>
          </Link>
        </motion.div>
      </motion.div>
      <motion.div
        className="about-image-container"
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <img 
          src={aboutUsImage} 
          alt="Sustainability Team" 
          className="about-image"
        />
      </motion.div>
    </div>
  </section>
);

const MilestonesSection = () => {
  const milestones = [
    {
      number: 30,
      text: "Years of Excellence",
      icon: FaTrophy,
      description: "Pioneering sustainable solutions"
    },
    {
      number: 14,
      text: "OEM Approvals",
      icon: FaCheckCircle,
      description: "Trusted by global manufacturers"
    },
    {
      number: 4,
      text: "Patented Products",
      icon: FaFileAlt,
      description: "Proprietary innovation portfolio"
    },
    {
      number: 21,
      text: "NDAs with Global MNCs",
      icon: FaHandshake,
      description: "Strategic industry partnerships"
    }
  ];

  const [flippedCards, setFlippedCards] = React.useState([]);
  const [counts, setCounts] = React.useState(milestones.map(() => 0));
  const countersRef = React.useRef([]);
  const [isMobile, setIsMobile] = React.useState(false);

  // Check if device is mobile
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Function to handle card flipping
  const handleCardFlip = (index, isFlipped) => {
    if (isFlipped) {
      setFlippedCards(prev => [...prev, index]);
    } else {
      setFlippedCards(prev => prev.filter(item => item !== index));
    }
  };

  // Handle touch for mobile devices
  const handleTouch = (index) => {
    if (isMobile) {
      // Toggle flipped state on mobile touch
      if (flippedCards.includes(index)) {
        handleCardFlip(index, false);
      } else {
        // Close any previously flipped card
        setFlippedCards([index]);
      }
    }
  };

  // Optimized counter animation function
  const animateCounter = (index) => {
    const target = milestones[index].number;
    const duration = 1500; // ms
    const frameDuration = 1000 / 60; // 60fps
    const totalFrames = Math.round(duration / frameDuration);
    
    let frame = 0;
    const counter = { value: 0 };
    
    const animate = () => {
      frame++;
      // Use easing function for smoother count-up
      const progress = frame / totalFrames;
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      counter.value = Math.floor(easeOutQuart * target);
      
      setCounts(prev => {
        const newCounts = [...prev];
        newCounts[index] = counter.value;
        return newCounts;
      });
      
      if (frame < totalFrames) {
        countersRef.current[index] = requestAnimationFrame(animate);
      }
    };
    
    countersRef.current[index] = requestAnimationFrame(animate);
  };

  // Intersection Observer for triggering animations when cards are in view
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          // Stagger the animations when section comes into view
          milestones.forEach((_, index) => {
            const delay = 300 + (index * 150);
            setTimeout(() => animateCounter(index), delay);
          });
        }
      },
      { threshold: 0.3 }
    );
    
    const sectionEl = document.querySelector('.milestones-section');
    if (sectionEl) observer.observe(sectionEl);
    
    return () => {
      if (sectionEl) observer.unobserve(sectionEl);
      // Clean up all animations
      countersRef.current.forEach(id => {
        if (id) cancelAnimationFrame(id);
      });
    };
  }, []);
  
  // Clean up animations when component unmounts
  React.useEffect(() => {
    return () => {
      countersRef.current.forEach(id => {
        if (id) cancelAnimationFrame(id);
      });
    };
  }, []);

  return (
    <section className="milestones-section">
      <div className="milestone-gradient-bg"></div>
      <div className="container">
        <div className="section-header">
          <motion.h2 
            className="section-title milestone-title-special"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Key Milestones
          </motion.h2>
        </div>
        
        <div className="milestones-special-container">
          {milestones.map((milestone, index) => (
            <motion.div
              key={milestone.text}
              className="milestone-3d-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ 
                opacity: 1, 
                y: 0
              }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: index * 0.2 }}
              onHoverStart={() => {
                if (!isMobile) {
                  handleCardFlip(index, true);
                }
              }}
              onHoverEnd={() => {
                if (!isMobile) {
                  handleCardFlip(index, false);
                }
              }}
              onTap={() => handleTouch(index)}
            >
              <div className={`milestone-inner ${flippedCards.includes(index) ? 'is-flipped' : ''}`}>
                <div className="milestone-front">
                  <div className="milestone-icon-wrap">
                    <div className="milestone-icon-circle">
                      <milestone.icon size={24} />
                    </div>
                  </div>
                  <h3 className="milestone-card-title">{milestone.text}</h3>
                  <div className="milestone-number-special">
                    <span className="milestone-counter">{counts[index]}</span>
                    {milestone.text === "Years of Excellence" && counts[index] === milestone.number && "+"}
                  </div>
                  {isMobile && (
                    <span className="milestone-tap-hint">
                      <FaFingerprint size={12} /> Tap to flip
                    </span>
                  )}
                </div>
                <div className="milestone-back">
                  <p className="milestone-description">{milestone.description}</p>
                  <div className="milestone-back-decoration"></div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const IndustriesSection = () => {
  const clientLogos = [
    { name: "Benninger", logo: benningerLogo },
    { name: "BEST Undertaking", logo: bestLogo },
    { name: "Bharat Electronics", logo: bharatElectronicsLogo },
    { name: "Grasim", logo: grasimLogo },
    { name: "Honda", logo: hondaLogo },
    { name: "Kirloskar", logo: kirloskarLogo },
    { name: "MDL", logo: mdlLogo },
    { name: "RCF", logo: rcfLogo }
  ];

  const industries = [
    {
      title: "Fragrance & Flavors"
    },
    {
      title: "Bulk Drugs & Pharmaceuticals"
    },
    {
      title: "Automotive OEMs & Components"
    },
    {
      title: "Electronics & LED"
    },
    {
      title: "EV & Smart Appliances"
    },
    {
      title: "Lubricant & Coolant"
    }
  ];

  return (
    <section className="industries-section">
      <div className="container">
        <motion.h2 
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Industries We Serve
        </motion.h2>

        <motion.div 
          className="industries-overview"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {industries.map((industry, index) => (
            <motion.div
              key={industry.title}
              className="industry-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <h3 className="industry-title">{industry.title}</h3>
            </motion.div>
          ))}
        </motion.div>

        {/* Trusted Clients Section */}
        <motion.div 
          className="trusted-clients"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h4 className="clients-title">Trusted by Industry Leaders</h4>
          <div className="clients-logos-container">
            <div className="clients-logos-track">
              {[...clientLogos, ...clientLogos].map((client, index) => (
                <motion.div
                  key={`${client.name}-${index}`}
                  className="client-logo-item"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.1, transition: { duration: 0.2 } }}
                >
                  <img 
                    src={client.logo} 
                    alt={`${client.name} logo`}
                    className="client-logo"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        <Link to="/expertise" className="view-more-btn">
          View Industry Applications <span className="arrow">&rarr;</span>
        </Link>
      </div>
    </section>
  );
};

const CommitmentSection = () => (
  <section className="commitment-section">
    <div className="container">
      <div className="commitment-content">
        <motion.div 
          className="commitment-text"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h4 className="section-title commitment-title">Our Commitment to Eco-Friendly Innovation</h4>  
          <p className="commitment-description content-justified">
            We craft eco solutions based on green chemistry principles, empowering industries to enhance EHS performance and sustainability.
          </p>
        </motion.div>
        <div className="commitment-image">
          <img src={ecoFriendlyImage} alt="Green Chemistry Innovation" />
        </div>
      </div>
    </div>
  </section>
);

const CorporateHighlights = () => {
  const highlights = [
    {
      icon: <FaLeaf size={28} />,
      text: "Largest Manufacturer of Iso Amyl Amine & Iso Amyl Chloride"
    },
    {
      icon: <FaTint size={28} />,
      text: "Mineral Oil-Free Coolants"
    },
    {
      icon: <FaCogs size={28} />,
      text: "Solvent & Abrasive-Free WLOR Cleaners"
    },
    {
      icon: <FaFlask size={28} />,
      text: "Patented Eco-Solutions with Customized Application Support"
    }
  ];

  return (
    <section className="highlights-section">
      <div className="container">
        <div className="section-header">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Corporate Highlights
          </motion.h2>
        </div>
        <div className="highlights-grid content-justified">
          {highlights.map((highlight, index) => (
            <motion.div
              key={highlight.text}
              className="highlight-item"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.25, type: 'spring', bounce: 0.4 }}
            >
              <div className="highlight-icon">{highlight.icon}</div>
              <div className="highlight-text"><b>{highlight.text}</b></div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => (
  <section className="contact-section">
    <div className="container">
      <div className="contact-content">
        <motion.div 
          className="contact-text"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title contact-title">Talk to Our Technical Experts</h2>
          <p className="contact-description content-justified">
            Looking for tailored, eco-friendly chemical solutions?
            Connect with our experienced technical team to discuss your process challenges and sustainability goals.
          </p>
          <Link to="/contact" className="contact-btn">
            Contact Our Experts <span className="arrow">&rarr;</span>
          </Link>
        </motion.div>
        <motion.div 
          className="contact-image"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <img src={contactUsImage} alt="Our Technical Experts" />
        </motion.div>
      </div>
    </div>
  </section>
);

const Hero = () => {
  const tours = [
    {
      title: "Aromatic Chemicals & Derivatives",
      description: "Leading manufacturer of Iso Amyl Alcohol and its natural esters for fragrance, flavors, bulk drugs, and pharmaceuticals.",
      imageSrc: aromaticImage,
      link: "/business/aromatic-chemicals"
    },
    {
      title: "Eco-Friendly & Sustainable Solutions",
      description: "Eco alternatives for OEMs & manufacturers — replacing hazardous solvents, acids, and alkalis with high-performance, green parts cleaning & surface finishing chemicals.",
      imageSrc: AdditivesImage,
      link: "/business/eco-friendly"
    },
    {
      title: "Heat Dissipation Compounds",
      description: "Advanced nano-based heat sink compounds and thermal pastes for improving heat management in electronics, LEDs, EVs, and appliances.",
      imageSrc: HeatDissipationImage,
      link: "/business/heat-dissipation"
    },
    {
      title: "Green Metal Working Additives",
      description: "Biodegradable lubricity additives, boundary lubricants, and corrosion inhibitors for synthetic and semi-synthetic metalworking fluids.",
      imageSrc: GreenMetalWorkingImage,
      link: "/business/metal-working"
    }
  ];

  return (
    <>
      <SEO
        title="Nimble Group - Leading Eco-Friendly Chemical Solutions | 25+ Years Excellence"
        description="Pioneering sustainable chemical solutions with 25+ years of expertise. Specializing in aromatic chemicals, heat dissipation, eco-friendly solutions, and green metal working additives for industries worldwide."
        keywords="eco-friendly chemicals, sustainable solutions, green chemistry, aromatic chemicals, heat dissipation, metal working fluids, environmental responsibility, chemical manufacturing, India"
        canonical="/"
        structuredData={[organizationSchema, websiteSchema]}
      />
      <FontLoader />
      <section className="hero">
        <div className="hero-content">
          <motion.h1 
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="hero-title-main">NIMBLE  GROUP</span>
            <br />
            <span className="hero-title-sub">
              Sustainable Solutions Through<br />
              Green Chemistry
            </span>
          </motion.h1>
          <Link to="/business" style={{ width: "100%" }}>
            <motion.button 
              className="book-now-btn"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              EXPLORE OUR VERTICAL BUSINESS VERTICALS <span className="arrow">&rarr;</span>
            </motion.button>
          </Link>
          <motion.div 
            className="scroll-indicator"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {/* <span className="scroll-text">Scroll Down</span> */}
            {/* <span className="scroll-arrow">↓</span> */}
          </motion.div>
        </div>
      </section>

      <AboutSection />
      
      <MilestonesSection />

      <section className="tours-section">
        <div className="container">
          <div className="section-header">
            <motion.h2 
              className="section-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Our Business Verticals
            </motion.h2>
          </div>
          <div className="tours-grid content-justified">
            {tours.map((tour, index) => (
              <motion.div
                key={tour.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <TourCard {...tour} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <IndustriesSection />
      <CommitmentSection />
      <CorporateHighlights />
      <ContactSection />
      <Footer />
    </>
  );
};

export default Hero; 